import React from "react";
import { Draggable } from "react-beautiful-dnd";

// Card do lead, renderizado por variante (sunlit / funnel / soft).
// Estilos inline pixel-perfect conforme README §1a/1b/1c.
// `card` é o modelo decorado (decorate.js). `showMsg` controla a linha da msg.

const ellipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Borda esquerda âmbar destacando cards SEM atendente.
const unassignedEdge = (hasAttendant) =>
  hasAttendant ? undefined : "4px solid #F5A623";

// Atendente do card: avatar + nome quando atribuído; chip âmbar "Sem atendente"
// (com dot de alerta) quando não há atendente — pra saltar aos olhos.
const Attendant = ({ card, size, radius }) => {
  if (!card.hasAttendant) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: "#FFF3DC",
          color: "#B7791F",
          fontSize: 10.5,
          fontWeight: 800,
          padding: "2px 9px",
          borderRadius: 999,
          flex: "none",
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#F5A623",
          }}
        />
        Sem atendente
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
      }}
    >
      <span
        title={card.agName}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          background: card.agColor,
          color: "#fff",
          fontSize: size <= 22 ? 9.5 : 10,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        {card.agInitials}
      </span>
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: "#5B6472",
          maxWidth: 96,
          ...ellipsis,
        }}
      >
        {card.agName}
      </span>
    </span>
  );
};

const SunlitCard = ({ card, showMsg, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "#fff",
      borderRadius: 14,
      borderLeft: unassignedEdge(card.hasAttendant),
      padding: "12px 14px",
      boxShadow: "0 1px 3px rgba(60,50,30,.09)",
      cursor: "grab",
      display: "flex",
      flexDirection: "column",
      gap: 7,
    }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#33302B" }}>
          {card.name}
        </div>
        <div style={{ fontSize: 11.5, color: "#9B9486", marginTop: 1 }}>
          {card.phone}
        </div>
      </div>
      <span
        title={card.prioLabel}
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: card.prioColor,
          marginTop: 5,
          flex: "none",
        }}
      />
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {card.tag && (
        <span
          style={{
            background: card.tagBg,
            color: card.tagColor,
            fontSize: 10.5,
            fontWeight: 800,
            padding: "2px 9px",
            borderRadius: 999,
          }}
        >
          {card.tag}
        </span>
      )}
      {card.chLabel && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "#F4EFE4",
            color: "#6B6456",
            fontSize: 10.5,
            fontWeight: 800,
            padding: "2px 9px",
            borderRadius: 999,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: card.chColor,
            }}
          />
          {card.chLabel}
        </span>
      )}
    </div>
    {showMsg && card.msg && (
      <div
        style={{
          fontSize: 12,
          color: "#8B8577",
          fontStyle: "italic",
          ...ellipsis,
        }}
      >
        {"“" + card.msg + "”"}
      </div>
    )}
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}
    >
      <Attendant card={card} size={24} radius="50%" />
      <span style={{ fontSize: 13, fontWeight: 800, color: "#2E7D4F" }}>
        {card.valueFmt}
      </span>
      <span style={{ flex: 1 }} />
      <span
        style={{
          background: card.slaBg,
          color: card.slaText,
          fontSize: 11,
          fontWeight: 800,
          padding: "2px 9px",
          borderRadius: 999,
        }}
      >
        {card.timeFmt}
      </span>
    </div>
  </div>
);

const FunnelCard = ({ card, showMsg, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "#fff",
      border: "1px solid #E6EAF0",
      borderLeft: unassignedEdge(card.hasAttendant),
      borderRadius: 10,
      padding: "11px 12px",
      cursor: "grab",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#151B26" }}>
          {card.name}
        </div>
        <div style={{ fontSize: 11, color: "#98A2B3", marginTop: 1 }}>
          {card.phone}
        </div>
      </div>
      {card.prioLabel && (
        <span
          style={{
            background: card.prioBg,
            color: card.prioColor,
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 5,
            flex: "none",
          }}
        >
          {card.prioLabel}
        </span>
      )}
    </div>
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {card.tag && (
        <span
          style={{
            background: card.tagBg,
            color: card.tagColor,
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 5,
          }}
        >
          {card.tag}
        </span>
      )}
      {card.chLabel && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "#F4F6F9",
            color: "#3D4756",
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 7px",
            borderRadius: 5,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: card.chColor,
            }}
          />
          {card.chLabel}
        </span>
      )}
    </div>
    {showMsg && card.msg && (
      <div style={{ fontSize: 11.5, color: "#7A8496", ...ellipsis }}>
        {card.msg}
      </div>
    )}
    <div
      style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 1 }}
    >
      <Attendant card={card} size={22} radius={7} />
      <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1F2733" }}>
        {card.valueFmt}
      </span>
      <span style={{ flex: 1 }} />
      <span
        style={{
          background: card.slaBg,
          color: card.slaText,
          fontSize: 10.5,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 6,
        }}
      >
        {card.timeFmt}
      </span>
    </div>
  </div>
);

const SoftCard = ({ card, showMsg, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "#fff",
      borderRadius: 18,
      borderLeft: unassignedEdge(card.hasAttendant),
      padding: "14px 15px 12px",
      boxShadow: "0 6px 18px rgba(80,60,120,.09)",
      cursor: "grab",
      display: "flex",
      flexDirection: "column",
      gap: 7,
    }}
  >
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#322A44" }}>
          {card.name}
        </div>
        <div style={{ fontSize: 11.5, color: "#9A92AB", marginTop: 1 }}>
          {card.phone}
        </div>
      </div>
      <Attendant card={card} size={26} radius="50%" />
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {card.tag && (
        <span
          style={{
            background: card.tagBg,
            color: card.tagColor,
            fontSize: 10.5,
            fontWeight: 800,
            padding: "2px 9px",
            borderRadius: 999,
          }}
        >
          {card.tag}
        </span>
      )}
      {card.chLabel && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "#F3F1F8",
            color: "#5A4B6E",
            fontSize: 10.5,
            fontWeight: 700,
            padding: "2px 9px",
            borderRadius: 999,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: card.chColor,
            }}
          />
          {card.chLabel}
        </span>
      )}
    </div>
    {showMsg && card.msg && (
      <div
        style={{
          fontSize: 12,
          color: "#8E86A0",
          fontStyle: "italic",
          ...ellipsis,
        }}
      >
        {"“" + card.msg + "”"}
      </div>
    )}
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: "#3D3350" }}>
        {card.valueFmt}
      </span>
      <span
        title={card.prioLabel}
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: card.prioColor,
          flex: "none",
        }}
      />
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 11.5, fontWeight: 800, color: card.slaText }}>
        {card.timeFmt}
      </span>
    </div>
    <div
      style={{
        height: 4,
        borderRadius: 2,
        background: "#EEEBF4",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          width: card.slaPct,
          background: card.slaBar,
          borderRadius: 2,
        }}
      />
    </div>
  </div>
);

const VARIANT_CARD = {
  sunlit: SunlitCard,
  funnel: FunnelCard,
  soft: SoftCard,
};

const KanbanCard = ({ card, index, variant, showMsg, onClick }) => {
  const Inner = VARIANT_CARD[variant] || FunnelCard;
  return (
    <Draggable draggableId={String(card.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            outline: "none",
            opacity: snapshot.isDragging ? 0.95 : 1,
          }}
        >
          <Inner card={card} showMsg={showMsg} onClick={onClick} />
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
